import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Group,
  Text,
  Stack,
  Loader,
  Button,
  Card,
  Avatar,
  SimpleGrid,
  Grid,
  Pagination,
  Flex,
  Box,
} from "@mantine/core";
import {
  IconSearch,
  IconUserOff,
  IconUserPlus,
  IconMapPin,
  IconMail,
  IconPhone,
} from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { fetchCustomers } from "../store/slices/customersSlice";
import { CustomerResponse } from "types/responses.type";

const Customers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: customers,
    loading,
    error,
  } = useSelector((state: RootState) => state.customers);
  const [search, setSearch] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState<
    CustomerResponse[]
  >([]);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  const paginatedCustomers = filteredCustomers.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    setFilteredCustomers([
      ...customers.filter((customer) =>
        customer.displayName.toLowerCase().includes(search.toLowerCase())
      ),
    ]);
    setActivePage(1); // Reset to first page on search or customer list change
  }, [search, customers]);

  const handleAddCustomer = () => {
    // Logic for adding a new customer (e.g., open a modal)
    console.log("Add New Customer clicked");
  };

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>Customers</Title>
        </Group>
        <Grid justify="space-between" align="center">
          {customers.length > 0 && (
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput
                placeholder="Search customers..."
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                w={"100%"}
              />
            </Grid.Col>
          )}
          <Box style={{ flexGrow: 1 }}></Box>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Flex justify="flex-end">
              <Pagination
                total={Math.ceil(filteredCustomers.length / itemsPerPage)}
                value={activePage}
                onChange={setActivePage}
              />
            </Flex>
          </Grid.Col>
        </Grid>

        {loading && <Loader />}
        {error && <Text c="red">{error}</Text>}
        {!loading && customers.length === 0 && (
          <Stack align="center" justify="center" style={{ height: "50vh" }}>
            <IconUserOff size={48} color="gray" />
            <Text ta="center" c="dimmed">
              No customers found. Click the "Add New Customer" button to add a
              new customer.
            </Text>
          </Stack>
        )}
        {!loading && paginatedCustomers.length > 0 && (
          <Grid justify="stretch">
            {paginatedCustomers.map((customer) => (
              <Grid.Col
                key={`customer-${customer.customerId}`}
                span={{ base: 12, lg: 6 }}
              >
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  h={"100%"}
                >
                  <Group>
                    <Avatar color="blue" radius="xl">
                      {customer.displayName[0]}
                    </Avatar>
                    <Stack gap={0}>
                      <Text fw={500}>{customer.displayName}</Text>
                      {customer.defaultPlace?.address && (
                        <Group gap="xs">
                          <IconMapPin size={16} color="gray" />
                          <Text>{customer.defaultPlace?.address}</Text>
                        </Group>
                      )}
                      {customer.email && (
                        <Group gap="xs">
                          <IconMail size={16} color="gray" />
                          <Text size="sm" c="dimmed">
                            {customer.email}
                          </Text>
                        </Group>
                      )}
                      {customer.contactPhoneNumber && (
                        <Group gap="xs">
                          <IconPhone size={16} color="gray" />
                          <Text size="sm" c="dimmed">
                            {customer.contactPhoneNumber}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

export default Customers;
